import { Box, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'

import { Button, Icon, IllustratedMessage, Modal, ModalProps, Radio, SearchInput } from '@percent/lemonade'
import classes from './OpportunityPickerModal.module.scss'
import type { OpportunitySelfParticipationDto } from '@percent/workplace-giving/api/opportunity/dto'
import { OpportunityOptionResult } from '@percent/workplace-giving/common/components/OpportunityOptionResult/OpportunityOptionResult'
import { OpportunityOptionsSkeleton } from './OpportunityOptionsSkeleton'

type OpportunityPickerModalProps = Pick<ModalProps, 'open' | 'onClose'> & {
  options?: OpportunitySelfParticipationDto[] | undefined
  defaultOption?: OpportunitySelfParticipationDto['opportunity']
  onConfirm: (opportunity: OpportunitySelfParticipationDto['opportunity']) => void
  searchValue: string
  isLoading: boolean
  setSearchValue: (value: string) => void
  hasErred: boolean
}

export function OpportunityPickerModal({
  open,
  onClose,
  options,
  defaultOption,
  searchValue,
  setSearchValue,
  isLoading,
  onConfirm,
  hasErred
}: Readonly<OpportunityPickerModalProps>) {
  const { t } = useTranslation()
  const [selectedOpportunity, setSelectedOpportunity] = useState<
    OpportunitySelfParticipationDto['opportunity'] | undefined
  >(options?.find(({ opportunity }) => opportunity.id === defaultOption?.id)?.opportunity)

  useEffect(() => {
    document.body.setAttribute('style', `overflow-y:${open ? 'hidden' : 'auto'}`)
  }, [open])

  const handleConfirm = () => {
    if (!selectedOpportunity) return

    onConfirm(selectedOpportunity)

    if (onClose) onClose()
  }

  return (
    <Modal borderRadiiSize="xlarge" onClose={onClose} open={open} data-testid="opportunity-picker-modal">
      <Box className={classes.ModalContent}>
        <Box className={classes.HeaderRowWrapper}>
          <Box className={classes.HeaderRow}>
            <Text component="h3" className={classes.Header}>
              {t('workplace_giving.volunteering.activityForm.opportunityModal.title')}
            </Text>
            <Box className={classes.CloseBtn} onClick={onClose}>
              <Icon name="close" size={4} />
            </Box>
          </Box>
        </Box>

        <Box>
          <Text className={classes.Label}>{t('workplace_giving.volunteering.activityForm.opportunityId.label')}</Text>
          <SearchInput
            aria-label="search"
            defaultValue={searchValue}
            onChange={e => {
              setSearchValue(e.target.value)
            }}
            handleClearValue={() => setSearchValue('')}
            borderRadiiSize="large"
          />
        </Box>

        <Box className={classes.MinHeightContent}>
          {isLoading && <OpportunityOptionsSkeleton />}
          {!isLoading && !!options?.length && (
            <>
              {options.map(({ opportunity }) => (
                <OpportunityOptionResult
                  prefix={
                    <Radio
                      label=""
                      value={opportunity.id}
                      checked={selectedOpportunity?.id === opportunity.id}
                      readOnly
                    />
                  }
                  handleClick={() => setSelectedOpportunity(opportunity)}
                  key={opportunity.id}
                  id={opportunity.id}
                  img={opportunity.image}
                  name={opportunity.name}
                  endDate={opportunity.endDate}
                />
              ))}
            </>
          )}

          {!isLoading && !hasErred && !options?.length && (
            <IllustratedMessage
              illustration="no-results"
              title={t('workplace_giving.common.noResultsFound')}
              description={t('workplace_giving.wizard.coverImage.findImage.noResults')}
            />
          )}

          {!isLoading && hasErred && (
            <IllustratedMessage
              illustration="invalid-link"
              title={t('workplace_giving.errors.somethingWentWrong')}
              description={t('workplace_giving.errors.tryAgainLater')}
            />
          )}
        </Box>

        <Box className={classes.Footer}>
          <Box className={classes.ButtonsRow}>
            <Button data-testid="cancel-btn" variant="secondary" shape="oval" onClick={onClose}>
              {t('workplace_giving.common.cancel')}
            </Button>
            <Button
              data-testid="select-opportunity-btn"
              variant="primary"
              shape="oval"
              disabled={!selectedOpportunity || hasErred || !options?.length}
              onClick={handleConfirm}
            >
              {t('workplace_giving.common.confirm')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
